import React, { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { API_DOMAIN } from "config";

const getNotificationId = (notification) =>
  notification?.id ?? notification?.notification_id ?? notification?.notif_id;

const mergeNotifications = (currentItems, incomingItems) => {
  const byId = new Map();
  [...incomingItems, ...currentItems].forEach((item) => {
    const id = getNotificationId(item);
    if (id != null && !byId.has(String(id))) byId.set(String(id), item);
  });
  return Array.from(byId.values());
};

export default function NotificationsBell({ currentUser }) {
  const [items, setItems] = useState([]);
  const [toastQueue, setToastQueue] = useState([]);
  const [open, setOpen] = useState(false);
  const soundRef = useRef(null);
  const knownRef = useRef(new Set());
  const isPollingReadyRef = useRef(false);
  const canPlaySoundRef = useRef(false);

  /** טעינת סאונד פעם אחת */
  useEffect(() => {
    soundRef.current = new Audio("/sounds/notification.mp3");
  }, []);

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // טעינת אודיו + פתיחת הרשאה לניגון אחרי אינטראקציה ראשונה
  useEffect(() => {
    const audio = new Audio("/sounds/notification.mp3");
    soundRef.current = audio;

    const unlockAudio = async () => {
      try {
        audio.muted = true;
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;

        canPlaySoundRef.current = true;

        window.removeEventListener("click", unlockAudio);
        window.removeEventListener("keydown", unlockAudio);
      } catch (e) {
        console.warn("unlock audio failed", e);
      }
    };

    window.addEventListener("click", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    return () => {
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  const playSound = () => {
    const audio = soundRef.current;
    if (!audio) return;
    if (!canPlaySoundRef.current) return;

    try {
      audio.currentTime = 0;
      const p = audio.play();
      if (p && typeof p.then === "function") p.catch(() => {});
    } catch (e) {
      console.warn("sound error", e);
    }
  };

  /** שליפה מלאה לבוקס */
  const loadNotifications = async () => {
    if (!currentUser?.id) return;
    try {
      const { data } = await axios.get(
        `${API_DOMAIN}/notification/getUserNotifications`,
        { params: { user_id: currentUser.id } }
      );
      if (data.success) setItems(data.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  /** פולינג — רק חדשות */
  useEffect(() => {
    if (!currentUser?.id) return;

    knownRef.current = new Set();
    isPollingReadyRef.current = false;
    setToastQueue([]);

    const checkNew = async () => {
      try {
        const { data } = await axios.get(
          `${API_DOMAIN}/notification/getUserNotifications`,
          { params: { user_id: currentUser.id, only_unread: 1 } }
        );

        if (!data.success) return;

        const list = data.data || [];
        const known = knownRef.current;

        if (!isPollingReadyRef.current) {
          list.forEach((n) => {
            const id = getNotificationId(n);
            if (id != null) known.add(String(id));
          });
          setItems((prev) => mergeNotifications(prev, list));
          isPollingReadyRef.current = true;
          return;
        }

        const newOnes = list.filter(
          (n) => {
            const id = getNotificationId(n);
            return id != null && !known.has(String(id)) && Number(n.is_read) === 0;
          }
        );

        if (newOnes.length > 0) {
          newOnes.forEach((n) => known.add(String(getNotificationId(n))));

          setItems((prev) => mergeNotifications(prev, newOnes));
          setToastQueue((prev) => [...prev, ...newOnes]);

          playSound();
        }
      } catch (e) {
        console.error("Polling error", e);
      }
    };

    checkNew();
    const timer = setInterval(checkNew, 10000);
    return () => clearInterval(timer);
  }, [currentUser?.id]);

  /** סימון כהתראה נקראה */
const markAsRead = async (notifId, opts = { fromToast: false }) => {
  try {
    await axios.post(`${API_DOMAIN}/notification/markAsRead`, null, {
      params: { id: notifId, user_id: currentUser.id },
    });
  } catch {}

  // ✅ עדכון אופטימי ברשימה
  setItems((prev) =>
    prev.map((n) => (n.id === notifId ? { ...n, is_read: 1 } : n))
  );

  if (opts.fromToast) {
    // ✅ בטוסט: קודם מסמנים "נקרא" כדי לראות וי רגע
    setToastQueue((prev) =>
      prev.map((n) => (n.id === notifId ? { ...n, is_read: 1 } : n))
    );

    // ואז מורידים אחרי רגע קטן
    setTimeout(() => {
      setToastQueue((prev) => prev.filter((n) => n.id !== notifId));
    }, 900);
  } else {
    // ברשימה רגיל – מורידים מיד
    setToastQueue((prev) => prev.filter((n) => n.id !== notifId));
  }
};

  const unreadCount = items.filter((n) => Number(n.is_read) === 0).length;

  const carDisplay = (n) =>
    `${n.manufacturer || ""} ${n.model || ""} ${n.sub_model || ""} ${
      n.year || ""
    }`.trim();

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        const readDiff = Number(a.is_read) - Number(b.is_read);
        if (readDiff !== 0) return readDiff;
        return new Date(b.created_at) - new Date(a.created_at);
      }),
    [items]
  );

  const notifTitle = (n) => {
    if (n.type === "immediate_purchase_approved") return "רכישה מידית";
    if (n.type === "immediate_purchase_closed_other") return "ההצעה שלך נדחתה";
    if (n.type === "offer_approved") return "!ההצעה שלך אושרה";
    if (n.type === "offer_approved_other") return "!ההצעה שלך נדחתה";
    if (n.type === "new_offer") return "!קיבלת הצעה חדשה";
    return "התראה חדשה";
  };

const SeenBadge = ({ isRead, onClick }) => {
  const bg = isRead ? "rgb(237, 255, 216)" : "rgb(255, 233, 233)";
  const clr = isRead ? "rgb(119, 172, 59)" : "rgb(214, 31, 31)";

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center cursor-pointer transition"
      style={{ backgroundColor: bg }}
      title={isRead ? "נצפה" : "סמן כנצפה"}
      aria-label={isRead ? "נצפה" : "סמן כנצפה"}
    >
      {/* אייקון כמו בטבלה */}
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path
          d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z"
          stroke={clr}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="2.5" stroke={clr} strokeWidth="2" />
      </svg>
    </button>
  );
};
  return (
    <>
      {/* פעמון קבוע */}
      <div className="notifBell fixed top-4 left-4 z-[999] notif-root">
        <button
          type="button"
          onClick={() => {
            setOpen((o) => !o);
            setToastQueue([]);
            loadNotifications();
          }}
          className="notif-btn relative flex items-center justify-center rounded-full shadow-lg transition bg-black hover:bg-gray-900 w-11 h-11 md:w-12 md:h-12 lg:w-14 lg:h-14"
          aria-label="התראות"
          style={{ backgroundColor: 'black' }}
        >
          <img
            src="/images/img__8.svg"
            alt=""
            className="block w-5 h-5 md:w-6 md:h-6 lg:w-7 lg:h-7"
          />

          {unreadCount > 0 && (
            <span style={{color: 'white'}} className="notif-badge absolute -top-1 -right-1 min-w-[18px] h-[18px] md:min-w-[20px] md:h-[20px] px-1 rounded-full bg-red-600 text-white text-[11px] md:text-xs flex items-center justify-center leading-none">
              {unreadCount}
            </span>
          )}
        </button>

        {open && (
          <>
            {/* שכבת רקע למובייל (בדסקטופ שקוף ולא מפריע) */}
            <div
              className="notif-overlay"
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />

            <div className="notifPanel notif-panel mt-3 w-70 bg-white border border-gray-200 rounded-xl shadow-xl text-right overflow-hidden animate-fade-in">
              <div
               style={{direction: 'rtl'}}
               className="notif-panel-header flex justify-between items-center px-4 py-2 border-b bg-gray-50">
                <span className="font-semibold">התראות</span>

                <button
                  type="button"
                  className="notif-close-btn"
                  onClick={() => setOpen(false)}
                  aria-label="סגור"
                >
                  ✕
                </button>
              </div>

              {sortedItems.length === 0 && (
                <div className="p-4 text-center text-gray-500"
                style={{backgroundColor: 'white'}}>אין התראות</div>
              )}

              {sortedItems.length > 0 && (
                <ul className="notifList notif-list max-h-96 overflow-y-auto">
                  {sortedItems.map((n) => (
                    <li
                      key={n.id}
                      style={{backgroundColor: 'white'}}
                      className={`notif-item px-4 py-3 border-b last:border-none transition ${
                        Number(n.is_read) === 0
                          ? "notif-item--unread bg-blue-50"
                          : "bg-white"
                      }`}
                    >
                      <div className="flex flex-row-reverse items-start w-full gap-3">
                        <div className="flex-1 font-semibold text-sm text-right notif-title">
                          {notifTitle(n)}
                        </div>

  {Number(n.is_read) === 0 ? (
  <SeenBadge isRead={false} onClick={() => markAsRead(n.id)} />
) : (
  <SeenBadge isRead={true} onClick={() => {}} />
)}
                      </div>

                      <div className="notif-body mt-1 text-sm text-gray-700 text-right" dir="rtl">
                        <div className="notif-row">
                          <strong>רכב:&nbsp;</strong>
                          <span dir="ltr" className="inline-block notif-ltr">
                            {carDisplay(n)}
                          </span>
                        </div>

                        {n.offered_price && (
                          <div className="notif-row">
                            <strong>מחיר הצעה:&nbsp;</strong>
                            <span dir="ltr" className="inline-block notif-ltr">
                              ₪ {Number(n.offered_price).toLocaleString()}
                            </span>
                          </div>
                        )}

                        {n.asking_price && (
                          <div className="notif-row">
                            <strong>מחיר מבוקש:&nbsp;</strong>
                            <span dir="ltr" className="inline-block notif-ltr">
                              ₪ {Number(n.asking_price).toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="notif-date text-[11px] text-gray-400 mt-1 text-right">
                        {formatDate(n.created_at)}
                      </div>
                      {Number(n.is_read) === 0 && (
  <div className="notif-confirm" dir="rtl">
    <input
      id={`confirm-${n.id}`}
      type="checkbox"
      checked={false}
      onChange={() => markAsRead(n.id)}
    />
    <label htmlFor={`confirm-${n.id}`}>מאשר צפייה</label>
  </div>
)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>

      {/* טוסטים */}
      {toastQueue.length > 0 && (
        <div className="notifToasts notif-toasts fixed top-20 left-4 z-[10000] space-y-3">
          {toastQueue.map((n) => (
            <div
              key={n.id}
              className="notifToast notif-toast relative bg-white border border-gray-300 rounded-xl shadow-2xl px-4 py-3 animate-slide-in"
              style={{ backgroundColor: 'white' }}
            >
<div className="absolute top-2 left-3">
<SeenBadge isRead={Number(n.is_read) === 1} onClick={() => markAsRead(n.id, { fromToast: true })} /></div>

              <div className="flex flex-row-reverse items-start">
                <div className="flex-1 font-semibold text-sm text-right notif-title">
                  {notifTitle(n)}
                </div>
              </div>

              <div className="notif-body mt-1 text-sm text-gray-700 text-right" dir="rtl">
                <div className="notif-row">
                  <strong>רכב:&nbsp;</strong>
                  <span dir="ltr" className="inline-block notif-ltr">
                    {carDisplay(n)}
                  </span>
                </div>

                {n.offered_price && (
                  <div className="notif-row">
                    <strong>מחיר הצעה:&nbsp;</strong>
                    <span dir="ltr" className="inline-block notif-ltr">
                      ₪ {Number(n.offered_price).toLocaleString()}
                    </span>
                  </div>
                )}

                {n.asking_price && (
                  <div className="notif-row">
                    <strong>מחיר מבוקש:&nbsp;</strong>
                    <span dir="ltr" className="inline-block notif-ltr">
                      ₪ {Number(n.asking_price).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              <div className="notif-date text-[11px] text-gray-400 mt-1 text-right">
                {formatDate(n.created_at)}
              </div>
              {Number(n.is_read) === 0 && (
  <div className="mt-2 flex items-center gap-2 text-[12px] text-gray-700" dir="rtl">
    <input
      id={`toast-confirm-${n.id}`}
      type="checkbox"
      checked={false}
      style={{ accentColor: "rgb(119, 172, 59)" }}
      onChange={() => markAsRead(n.id, { fromToast: true })}
    />
    <label htmlFor={`toast-confirm-${n.id}`} className="cursor-pointer select-none">
      מאשר צפייה
    </label>
  </div>
)}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
